import { useEffect, useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

/**
 * UID-420 follow-up — `.parts-table-card`'s right-edge scroll-shadow
 * (`parts.css::after`) used to be unconditional (always painted, even when
 * the table fit with no horizontal overflow), which doesn't match the
 * design comp. Gates that shadow not just on the element being scrollable in
 * the abstract, but on there being more content to scroll to RIGHT NOW: true
 * only when the element's content is wider than its visible box AND the
 * user hasn't already scrolled to (or near) the right edge. This keeps the
 * shadow from permanently overlaying the last column (e.g. a row's
 * "more actions" button) once there is nothing left to scroll to.
 *
 * `REMAINING_SCROLL_THRESHOLD_PX` absorbs subpixel rounding: browsers can
 * report `scrollLeft` a fraction of a pixel short of its true max (esp. with
 * fractional zoom/DPI), which would otherwise leave `remaining` at ~0.3px
 * forever and make the shadow flicker between shown/hidden near the edge.
 *
 * Three effects, deliberately: the `useLayoutEffect` re-measures on EVERY
 * render (before paint, so there's no one-frame flash) — this is what
 * catches content-driven changes (e.g. a row added/removed resizes
 * `scrollWidth` without resizing `el`'s own box, which `ResizeObserver`
 * below never sees). The first `useEffect` only sets up the `ResizeObserver`
 * once per `el`, to catch container-driven changes (e.g. a window resize)
 * that don't necessarily trigger a React re-render at all. The second
 * `useEffect` attaches a `scroll` listener on `el`, once per `el`, to catch
 * scroll-position changes — the user scrolling the table — which are the
 * only thing that changes `scrollLeft` without changing size and so would
 * otherwise never be re-checked at all.
 *
 * The no-deps `useLayoutEffect` forces a synchronous layout read on every
 * render of every caller — an accepted, deliberate cost (correctness over a
 * micro-optimization) at this hook's current call sites (one table card per
 * screen), not something to reach for on a hot/high-frequency render path
 * without re-checking.
 *
 * PRECONDITION: `ref` must be attached to an element that stays mounted for
 * this hook's whole lifetime. `ref` (a `useRef` object) never changes
 * identity, so the `ResizeObserver`/`scroll`-listener attachment effects run
 * exactly once each; if `ref.current` is still null on that one run, neither
 * is ever attached or retried (the `useLayoutEffect` above still re-measures
 * on every render, so this only matters for container resizes/scrolls that
 * don't otherwise trigger a re-render). Every current caller renders its
 * ref'd element unconditionally, so this doesn't bite today — a future
 * caller with a conditionally-mounted ref would need a callback ref instead.
 */
const REMAINING_SCROLL_THRESHOLD_PX = 2

function hasMoreToScroll(el: HTMLElement): boolean {
  return el.scrollWidth - el.scrollLeft - el.clientWidth > REMAINING_SCROLL_THRESHOLD_PX
}

export function useIsScrollable(ref: RefObject<HTMLElement | null>): boolean {
  const [isScrollable, setIsScrollable] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setIsScrollable(hasMoreToScroll(el))
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new ResizeObserver(() => {
      setIsScrollable(hasMoreToScroll(el))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const handleScroll = () => setIsScrollable(hasMoreToScroll(el))
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [ref])

  return isScrollable
}
